import { api } from '@store/api';
import { VexStatus } from '@features/applications/applications.models';

import { impactedAppVersionsTag } from './quick-search.cache-tags';
import type {
  GetImpactedAppVersionsResponse,
  ImpactedAppVersionsFilters,
  ImpactedAppVersionsQueryArg,
  ImpactedAppVersionsSort,
  VulnerabilityLookupResponse,
} from './quick-search.models';
import { serializeImpactedAppVersionsFilters } from './quick-search.utils';

/**
 * Default sort for the impacted-app-versions list: by application and version
 * name, ascending.
 */
export const DEFAULT_IMPACTED_APP_VERSIONS_SORT: ImpactedAppVersionsSort = {
  versionName: false,
};

/**
 * Default filters: the VEX statuses that still warrant attention — versions
 * with no annotation, those explicitly affected, and those under triage.
 */
export const DEFAULT_IMPACTED_APP_VERSIONS_FILTERS: ImpactedAppVersionsFilters =
  {
    annotationStatus: [
      'none',
      VexStatus.Affected,
      VexStatus.UnderInvestigation,
    ],
  };

const quickSearchAPI = api.injectEndpoints({
  endpoints: builder => ({
    vulnerabilityLookup: builder.query<VulnerabilityLookupResponse, string>({
      query: id => ({
        url: '/quick-search/vulnerability-lookup',
        params: { id },
      }),
      transformResponse: (response: { data: VulnerabilityLookupResponse }) =>
        response.data,
    }),
    getVulnerabilityImpactStatus: builder.query<{ impacted: boolean }, string>({
      query: vulnerabilityId => ({
        url: '/quick-search/vulnerability-impact-status',
        params: { vulnerabilityId },
      }),
      transformResponse: (response: { data: { impacted: boolean } }) =>
        response.data,
    }),
    getImpactedAppVersions: builder.infiniteQuery<
      GetImpactedAppVersionsResponse,
      ImpactedAppVersionsQueryArg,
      string | null
    >({
      infiniteQueryOptions: {
        initialPageParam: null,
        getNextPageParam: lastPage => lastPage.nextCursor ?? undefined,
      },
      query: ({ queryArg: { vulnerabilityId, sort, filters }, pageParam }) => ({
        url: '/quick-search/vulnerabilities-search',
        params: {
          vulnerabilityId,
          ...(sort && { sort }),
          ...serializeImpactedAppVersionsFilters(filters),
          ...(pageParam && { cursor: pageParam }),
        },
      }),
      providesTags: (_, __, { vulnerabilityId }) => [
        impactedAppVersionsTag(vulnerabilityId),
      ],
    }),
  }),
});

export const {
  useLazyVulnerabilityLookupQuery,
  useVulnerabilityLookupQuery,
  useGetVulnerabilityImpactStatusQuery,
  useGetImpactedAppVersionsInfiniteQuery,
} = quickSearchAPI;

export { quickSearchAPI };
